import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell } from "../components/Shell";
import { api, errorMessage } from "../lib/api";
import { CITIES as FALLBACK_CITIES, formatDate } from "../constants/rox";
import { AdminCities } from "../components/AdminCities";
import { AdminStats } from "../components/AdminStats";
import { AdminReports } from "../components/AdminReports";
import { AdminDemo } from "../components/AdminDemo";
import { RESULT_FORMATS } from "../constants/rox";

const STATUS_OPTIONS = [["upcoming", "À venir (date confirmée)"], ["tbc", "À venir — date à confirmer"], ["postponed", "Reportée"], ["archived", "Historique (passée)"]];
const STATUS_LABELS = { past: "Terminée", archived: "Historique", postponed: "Reportée", tbc: "Date à confirmer" };
const FORMAT_LABELS = { solo_open: "Solo Open", solo_pro: "Solo Pro", doubles_men: "Doubles Hommes", doubles_women: "Doubles Femmes", doubles_mixed: "Doubles Mixtes", doubles_pro: "Doubles Pro", relay: "Relais" };

const EMPTY = { name: "", city: "", date: "", end_date: "", venue: "", status: "upcoming", formats: [], source_url: "" };

export default function AdminRaces() {
  const navigate = useNavigate();
  const [races, setRaces] = useState(null);
  const [cities, setCities] = useState(FALLBACK_CITIES);
  const loadCities = () => { api.get("/cities").then((r) => setCities(r.data)).catch(() => {}); };
  useEffect(loadCities, []);
  const CITIES = cities;
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => { api.get("/admin/races").then((r) => setRaces(r.data)).catch((e) => setError(errorMessage(e))); };
  useEffect(load, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const startEdit = (r) => { setEditing(r.id); setForm({ name: r.name, city: r.city || "", date: r.date || "", end_date: r.end_date || "", venue: r.venue || "", status: r.status === "past" ? "upcoming" : r.status || "upcoming", formats: r.formats || [], source_url: r.source_url || "" }); setError(""); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/races/${editing}`, form);
      else await api.post("/admin/races", form);
      cancel();
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer ${r.name} ?`)) return;
    setError("");
    try {
      await api.delete(`/admin/races/${r.id}`);
      load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <AppShell>
      <TopBar onBack={() => navigate("/profil")} />
      <main className="px-5 pb-16 pt-2" data-testid="admin-races-page">
        <p className="eyebrow text-rox-orange">Administration</p>
        <h1 className="h-display mt-1 text-4xl">Tableau de bord</h1>
        <p className="mt-2 text-sm text-rox-off/75">Crée ou modifie les courses, leurs dates et les villes. Tout apparaît immédiatement dans l'onboarding, les filtres, le countdown et le matching.</p>

        <div className="mt-8"><AdminStats /></div>
        <h2 className="h-display mt-10 text-3xl">Courses <span className="text-rox-orange">HYROX</span></h2>

        <form onSubmit={submit} className="panel mt-6 space-y-4 p-4" data-testid="race-form">
          <div className="flex items-center justify-between">
            <p className="h-display text-2xl">{editing ? "Modifier la course" : "Nouvelle course"}</p>
            {editing && <button type="button" onClick={cancel} data-testid="race-form-cancel" className="text-rox-muted hover:text-rox-off" aria-label="Annuler"><X size={18} /></button>}
          </div>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">Nom</span>
            <input data-testid="race-name-input" className="field" placeholder="HYROX Nantes" value={form.name} onChange={set("name")} required maxLength={60} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-2 block text-sm font-medium">Ville</span>
              <select data-testid="race-city-select" className="field appearance-none" value={form.city} onChange={set("city")} required={editing !== "other"}>
                <option value="" disabled>Ville</option>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                {form.city && !CITIES.includes(form.city) && <option value={form.city}>{form.city}</option>}
              </select>
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium">Date</span>
              <input data-testid="race-date-input" className="field" type="date" value={form.date} onChange={set("date")} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-2 block text-sm font-medium">Date de fin</span>
              <input data-testid="race-end-date-input" className="field" type="date" value={form.end_date} onChange={set("end_date")} />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium">Statut</span>
              <select data-testid="race-status-select" className="field appearance-none" value={form.status} onChange={set("status")}>
                {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">Lieu (optionnel)</span>
            <input data-testid="race-venue-input" className="field" placeholder="Paris Expo Porte de Versailles" value={form.venue} onChange={set("venue")} maxLength={160} />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">Source officielle (URL, optionnel)</span>
            <input data-testid="race-source-input" className="field" placeholder="https://hyroxfrance.com/event/..." value={form.source_url} onChange={set("source_url")} maxLength={300} />
          </label>
          <div>
            <span className="mb-2 block text-sm font-medium">Formats confirmés</span>
            <div className="flex flex-wrap gap-2">
              {RESULT_FORMATS.map((f) => (
                <button key={f} type="button" data-testid={`race-format-${f}`} data-active={form.formats.includes(f)} onClick={() => setForm({ ...form, formats: form.formats.includes(f) ? form.formats.filter((x) => x !== f) : [...form.formats, f] })}
                  className={`rounded-[3px] border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${form.formats.includes(f) ? "border-rox-off bg-rox-off text-rox-black" : "border-rox-line text-rox-off/80"}`}>{FORMAT_LABELS[f]}</button>
              ))}
            </div>
          </div>
          {error && <p data-testid="admin-error" className="text-sm text-rox-orange">{error}</p>}
          <button data-testid="race-form-submit" className="btn-primary py-3 text-base" disabled={busy}>
            {editing ? <Pencil size={18} /> : <Plus size={18} />} {editing ? "Enregistrer" : "Ajouter la course"}
          </button>
        </form>

        <div className="mt-6 space-y-2" data-testid="admin-races-list">
          {races?.map((r) => (
            <div key={r.id} data-testid={`admin-race-${r.id}`} className="panel flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="h-display text-xl">{r.name}</p>
                <p className="text-xs text-rox-muted">{r.date ? formatDate(r.date) : "Sans date"}{r.end_date ? ` → ${formatDate(r.end_date)}` : ""} · {r.athletes} athlète{r.athletes > 1 ? "s" : ""} réel{r.athletes > 1 ? "s" : ""}{r.status !== "upcoming" ? ` · ${STATUS_LABELS[r.status] || r.status}` : ""}</p>
              </div>
              <button data-testid={`admin-race-edit-${r.id}`} onClick={() => startEdit(r)} className="flex h-9 w-9 items-center justify-center rounded-[3px] border border-rox-line text-rox-off/80 hover:border-rox-orange hover:text-rox-orange" aria-label="Modifier"><Pencil size={15} /></button>
              {r.id !== "other" && (
                <button data-testid={`admin-race-delete-${r.id}`} onClick={() => remove(r)} className="flex h-9 w-9 items-center justify-center rounded-[3px] border border-rox-line text-rox-muted hover:border-rox-orange hover:text-rox-orange" aria-label="Supprimer"><Trash2 size={15} /></button>
              )}
            </div>
          ))}
        </div>
        <AdminDemo />
        <AdminCities onChanged={loadCities} />
        <AdminReports />
      </main>
    </AppShell>
  );
}
